import * as React from 'react'
import { ChevronLeft } from 'lucide-react'

import { Button } from '../../components/button'
import { FieldError } from '../../components/field'
import { FlowDialog } from '../../components/flow-dialog'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../../components/input-otp'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/sheet'
import { Spinner } from '../../components/spinner'
import { UserAvatar } from '../../components/user-avatar'
import { useIsMobile } from '../../hooks/use-mobile'
import { cn } from '../../lib/utils'

/** `input-otp` restricts what the hidden input accepts to this pattern. */
const DIGITS_ONLY = '^\\d+$'

const DEFAULT_LENGTH = 4

export interface PinPadProps {
  value: string
  onValueChange: (value: string) => void
  /** Fired once the pad fills. The host does the async work. */
  onComplete?: (value: string) => void
  /** Number of digits. Four is the house default. */
  length?: number
  disabled?: boolean
  /**
   * Draw a bullet over each filled slot instead of the digit. A PIN is a
   * credential, so this is on by default; turn it off only where the value
   * is not secret.
   */
  masked?: boolean
  id?: string
  'aria-label'?: string
  className?: string
}

/**
 * The PIN entry pad: a row of single-digit slots backed by one hidden input.
 *
 * Masking is drawn rather than swapped for a password field, so the slot keeps
 * its design-system border, active ring and caret: the character renders
 * transparent and a bullet is painted over it.
 */
export function PinPad({
  value,
  onValueChange,
  onComplete,
  length = DEFAULT_LENGTH,
  disabled,
  masked = true,
  id,
  'aria-label': ariaLabel = 'PIN',
  className,
}: PinPadProps) {
  const slots = React.useMemo(
    () => Array.from({ length }, (_, index) => index),
    [length]
  )

  return (
    <InputOTP
      id={id}
      aria-label={ariaLabel}
      maxLength={length}
      pattern={DIGITS_ONLY}
      inputMode="numeric"
      autoComplete="off"
      value={value}
      disabled={disabled}
      className={className}
      onChange={(next: string) => {
        onValueChange(next)
        if (next.length === length) {
          onComplete?.(next)
        }
      }}
    >
      <InputOTPGroup>
        {slots.map((index) => (
          <InputOTPSlot
            key={index}
            index={index}
            className={cn(
              'size-12 text-base',
              masked &&
                index < value.length &&
                "text-transparent after:absolute after:inset-0 after:flex after:items-center after:justify-center after:text-foreground after:content-['•']"
            )}
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  )
}

export interface PinLoginUser {
  id: string
  first?: string | null
  last?: string | null
  email?: string | null
}

export interface UserPinLoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The org's users, in any order — sorted alphabetically internally. */
  users: PinLoginUser[]
  isLoadingUsers?: boolean
  /** Fired once a user is picked AND the pad fills. The host runs the
   *  request and reports back through `error` / `isChecking`. */
  onComplete: (userId: string, pin: string) => void
  title?: string
  /** Shown on the select step; the pin step gets its own generated from
   *  the selected user's name. */
  description?: React.ReactNode
  /** Shown under the pad. A fresh error also clears the pad for a retry. */
  error?: string | null
  isChecking?: boolean
  checkingLabel?: string
  length?: number
  resolveAvatarUrl?: (email: string) => string | null
}

function personLabel(user: PinLoginUser): string {
  const name = [user.first, user.last]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ')
  return name || user.email || 'Someone'
}

function sortedPinLoginUsers(users: PinLoginUser[]): PinLoginUser[] {
  return [...users].sort((a, b) => personLabel(a).localeCompare(personLabel(b)))
}

function PinLoginUserRow({
  user,
  onSelect,
  resolveAvatarUrl,
}: {
  user: PinLoginUser
  onSelect: () => void
  resolveAvatarUrl?: (email: string) => string | null
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
    >
      <UserAvatar
        user={{
          first_name: user.first ?? '',
          last_name: user.last ?? '',
          email: user.email ?? '',
        }}
        resolveAvatarUrl={resolveAvatarUrl}
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {personLabel(user)}
        </span>
        {user.email ? (
          <span className="truncate text-xs text-muted-foreground">
            {user.email}
          </span>
        ) : null}
      </div>
    </button>
  )
}

/**
 * OAuth-style PIN login: pick yourself from the org's user list, then enter
 * your PIN. The v2 API needs to know WHO is authenticating before it checks
 * the digits, so this owns two steps' worth of local state — but the host
 * still runs the actual request and reports progress back through
 * `isChecking` / `error`, same division of responsibility as `PinPad` itself.
 *
 * Renders as a bottom `Sheet` on mobile and a `FlowDialog` (back button +
 * animated height) on desktop, matching the shape of other multi-step flows
 * in the product.
 */
export function UserPinLoginDialog({
  open,
  onOpenChange,
  users,
  isLoadingUsers = false,
  onComplete,
  title = 'Switch user',
  description = 'Select yourself, then enter your PIN.',
  error,
  isChecking = false,
  checkingLabel = 'Checking PIN…',
  length = DEFAULT_LENGTH,
  resolveAvatarUrl,
}: UserPinLoginDialogProps) {
  const isMobile = useIsMobile()
  const [step, setStep] = React.useState<'select' | 'pin'>('select')
  const [selectedUser, setSelectedUser] = React.useState<PinLoginUser | null>(
    null
  )
  const [pin, setPin] = React.useState('')

  const sortedUsers = React.useMemo(() => sortedPinLoginUsers(users), [users])

  // Forgets the selection and digits whenever the dialog closes, so
  // reopening always starts back at the user list, never a stale pin/step.
  React.useEffect(() => {
    if (!open) {
      setStep('select')
      setSelectedUser(null)
      setPin('')
    }
  }, [open])

  // A failed attempt empties the pad so the next one starts clean.
  React.useEffect(() => {
    if (error) setPin('')
  }, [error])

  const handleSelect = (user: PinLoginUser) => {
    setSelectedUser(user)
    setStep('pin')
  }

  const handleBack = () => {
    setStep('select')
    setSelectedUser(null)
    setPin('')
  }

  const handlePinComplete = (value: string) => {
    if (!selectedUser) return
    onComplete(selectedUser.id, value)
  }

  const activeTitle =
    step === 'pin' && selectedUser ? personLabel(selectedUser) : title
  const activeDescription =
    step === 'pin' ? 'Enter your PIN to continue.' : description

  const selectStep = (
    <div className="flex max-h-[60vh] min-h-0 flex-col gap-1 overflow-y-auto px-4 py-4 scroll-py-4">
      {isLoadingUsers ? (
        <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Spinner />
          Loading team members…
        </p>
      ) : (
        sortedUsers.map((user) => (
          <PinLoginUserRow
            key={user.id}
            user={user}
            onSelect={() => handleSelect(user)}
            resolveAvatarUrl={resolveAvatarUrl}
          />
        ))
      )}
    </div>
  )

  const pinStep = (
    <div className="flex flex-col items-center gap-4 px-4 py-4">
      <PinPad
        value={pin}
        onValueChange={setPin}
        onComplete={handlePinComplete}
        disabled={isChecking}
        length={length}
      />

      {error ? <FieldError className="text-center">{error}</FieldError> : null}

      {isChecking ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {checkingLabel}
        </p>
      ) : null}
    </div>
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex max-h-[90dvh] flex-col">
          <SheetHeader>
            {step === 'pin' ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ChevronLeft />}
                className="-ml-2 w-fit"
                onClick={handleBack}
              >
                Back
              </Button>
            ) : null}
            <SheetTitle>{activeTitle}</SheetTitle>
            {activeDescription ? (
              <SheetDescription>{activeDescription}</SheetDescription>
            ) : null}
          </SheetHeader>
          {step === 'select' ? selectStep : pinStep}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <FlowDialog
      open={open}
      onOpenChange={onOpenChange}
      title={activeTitle}
      description={activeDescription}
      onBack={step === 'pin' ? handleBack : undefined}
      className="new-ui-theme sm:max-w-sm"
    >
      {step === 'select' ? selectStep : pinStep}
    </FlowDialog>
  )
}
